// Port de la partie voiranime d'api.go. Pas de niveau saison ici : une fiche
// vaut une liste d'épisodes.

import { normalizeEmbedHost, voiranimeBase } from "../constants.ts";
import { fetchDocument, fetchText } from "../proxy.ts";
import { embedPriority, isSupportedEmbed } from "../resolvers.ts";
import type { AnimeHit } from "./animesama.ts";

export interface VoiranimeEpisode {
  name: string;
  url: string;
}

// Le domaine n'est connu qu'une fois `/config` lu : il se relit à chaque appel
// plutôt que d'être figé au chargement du module.
const referer = (): string => voiranimeBase() + "/";

export async function search(
  query: string,
  signal?: AbortSignal,
): Promise<AnimeHit[]> {
  const params = new URLSearchParams({ s: query, post_type: "wp-manga" });

  const doc = await fetchDocument(`${voiranimeBase()}/?${params}`, {
    referer: referer(),
    signal,
  });

  if (!doc) return [];

  const hits = new Map<string, string>();

  for (const anchor of doc.querySelectorAll(
    "div.c-tabs-item__content div.post-title h3 a[href]",
  )) {
    const name = anchor.textContent?.trim();
    if (name) hits.set(name, anchor.getAttribute("href")!);
  }

  return [...hits].map(([name, url]) => ({ name, url }));
}

// "Black Torch - 05 VOSTFR - 05" -> "05 VOSTFR"
const EPISODE_NAME_RE = /^.+ - (\d+) (VOSTFR|VF)(?: - \d+)?$/;

function cleanEpisodeName(raw: string): string {
  const name = raw.trim().split(/\s+/).join(" ");
  const match = EPISODE_NAME_RE.exec(name);

  return match ? `${match[1]} ${match[2]}` : name;
}

/** Retourne les épisodes du plus ancien au plus récent (le site les liste dans
 *  l'ordre inverse). */
export async function episodes(animeUrl: string): Promise<VoiranimeEpisode[]> {
  const doc = await fetchDocument(animeUrl, { referer: referer() });
  if (!doc) return [];

  const found: VoiranimeEpisode[] = [];

  for (const anchor of doc.querySelectorAll("li.wp-manga-chapter > a[href]")) {
    found.push({
      name: cleanEpisodeName(anchor.textContent ?? ""),
      url: anchor.getAttribute("href")!,
    });
  }

  return found.reverse();
}

const SOURCES_RE = /var\s+thisChapterSources\s*=\s*(\{[\s\S]*?\});/;
const IFRAME_SRC_RE = /src="([^"]+)"/;

/** Retourne les lecteurs supportés de la page épisode, du plus au moins
 *  prioritaire, à essayer dans l'ordre. */
export async function embeds(episodeUrl: string): Promise<string[]> {
  const body = await fetchText(episodeUrl, { referer: referer() });
  if (body === "") return [];

  const match = SOURCES_RE.exec(body);
  if (!match) return [];

  let sources: Record<string, string>;

  try {
    sources = JSON.parse(match[1]!) as Record<string, string>;
  } catch {
    return [];
  }

  const found: string[] = [];

  for (const iframe of Object.values(sources)) {
    const src = IFRAME_SRC_RE.exec(iframe);
    if (src && isSupportedEmbed(src[1]!)) found.push(normalizeEmbedHost(src[1]!));
  }

  return found.sort((a, b) => embedPriority(a) - embedPriority(b));
}
